import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Activity, Trash2, RefreshCw, Check, AlertTriangle, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_LABELS = {
  webhook_received: 'Webhook Received',
  signature_verified: 'Signature Verified',
  signature_failed: 'Signature Failed',
  ai_response_sent: 'AI Response Sent',
  ai_response_failed: 'AI Response Failed',
  api_key_updated: 'API Key Updated',
  api_key_removed: 'API Key Removed',
  user_login: 'User Login',
};

const STATUS_CONFIG = {
  success: { icon: Check, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400/10' },
  error: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10' },
};

function formatTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch {
    return ts;
  }
}

export default function ActivityLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchLog();
  }, []);

  const fetchLog = async () => {
    try {
      const { data } = await axios.get(`${API}/api/activity-log`, { withCredentials: true });
      setEntries(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLog();
    setRefreshing(false);
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await axios.delete(`${API}/api/activity-log`, { withCredentials: true });
      setEntries([]);
      setShowClearConfirm(false);
    } catch { /* ignore */ }
    finally { setClearing(false); }
  };

  return (
    <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] overflow-hidden transition-all duration-200 hover:border-[#7B2FFF]/30">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#27272A]">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#7B2FFF]" />
          <h3 className="text-sm font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Activity Log
          </h3>
          <span className="text-xs text-[#71717A] font-mono ml-1">
            ({entries.length})
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!showClearConfirm ? (
            <Button
              data-testid="clear-log-button"
              onClick={() => setShowClearConfirm(true)}
              disabled={entries.length === 0}
              variant="ghost"
              size="sm"
              className="text-[#71717A] hover:text-red-400 hover:bg-red-400/5 h-7 px-2.5 text-xs"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Clear Log
            </Button>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-[#A1A1AA]">Are you sure? This cannot be undone.</span>
              <Button
                data-testid="confirm-clear-log"
                onClick={handleClear}
                disabled={clearing}
                size="sm"
                className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 h-7 px-2.5 text-xs"
              >
                {clearing ? <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" /> : 'Yes, clear'}
              </Button>
              <Button
                data-testid="cancel-clear-log"
                onClick={() => setShowClearConfirm(false)}
                variant="ghost"
                size="sm"
                className="text-[#71717A] hover:text-[#EDEDF0] h-7 px-2 text-xs"
              >
                Cancel
              </Button>
            </div>
          )}
          <button
            data-testid="refresh-activity-log"
            onClick={handleRefresh}
            className="p-1.5 rounded-md text-[#71717A] hover:text-[#EDEDF0] hover:bg-[#ffffff08] transition-all"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Body */}
      <ScrollArea className="h-[320px]">
        <div className="divide-y divide-[#27272A]/50">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-5 h-5 border-2 border-[#7B2FFF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2" data-testid="activity-log-empty">
              <Activity className="w-6 h-6 text-[#27272A]" />
              <p className="text-sm text-[#71717A]">No activity yet</p>
              <p className="text-xs text-[#3f3f46]">Events will appear here as your bot processes messages</p>
            </div>
          ) : (
            entries.map((entry, i) => {
              const statusCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.success;
              const StatusIcon = statusCfg.icon;
              return (
                <div
                  key={i}
                  data-testid={`activity-entry-${i}`}
                  className="flex items-start gap-3 px-5 py-3 hover:bg-[#ffffff03] transition-colors"
                >
                  <div className={`w-6 h-6 rounded-md ${statusCfg.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <StatusIcon className={`w-3.5 h-3.5 ${statusCfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-medium text-[#EDEDF0]">
                        {EVENT_LABELS[entry.event_type] || entry.event_type}
                      </span>
                      <span className="text-xs text-[#3f3f46]">&middot;</span>
                      <span className="text-xs text-[#71717A] font-mono">
                        {formatTime(entry.timestamp)}
                      </span>
                    </div>
                    <p className="text-xs text-[#A1A1AA] truncate">{entry.message}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
